
import React, { useState, useEffect } from 'react';
import { HDate } from "@hebcal/core";
import { supabase } from "@/integrations/supabase/client";
import { HADERA_COORDINATES } from "@/types/time";
import SefiratHaOmerCounter from './pages/SefiratHaOmerCounter';
import { usePageContext } from '@/hooks/usePageContext';

interface MarqueeBarProps {
  parashaText: string;
}

const MarqueeBar: React.FC<MarqueeBarProps> = ({ parashaText }) => {
  console.log('MarqueeBar rendered with parashaText:', parashaText);
  const { titleConfig } = usePageContext();
  const [pirkeiAvotChapter, setPirkeiAvotChapter] = useState<string>("");
  const [isOmerPeriod, setIsOmerPeriod] = useState<boolean>(false);
  
  useEffect(() => {
    const calculatePirkeiAvotChapter = () => {
      const today = new Date();
      const hdate = new HDate(today);
      
      // Get the Hebrew year
      const hebrewYear = hdate.getFullYear();
      
      // Calculate Pesach date (15 Nissan)
      const pesach = new HDate(15, 1, hebrewYear); // 1 is Nissan
      const pesachDate = pesach.greg();
      
      // Calculate Erev Shavuot date (5 Sivan) - the last day of Omer counting
      const erevShavuot = new HDate(5, 3, hebrewYear); // 3 is Sivan
      const erevShavuotDate = erevShavuot.greg();
      
      // Check if we're between Pesach and Erev Shavuot (inclusive)
      // The Omer period runs from 16 Nissan through 5 Sivan (49 days)
      const inOmerPeriod = today >= pesachDate && today <= erevShavuotDate;
      console.log('Omer period check:', { 
        today: today.toDateString(), 
        todayHebrew: `${hdate.getDate()} ${hdate.getMonthName()} ${hdate.getFullYear()}`,
        pesachDate: pesachDate.toDateString(), 
        erevShavuotDate: erevShavuotDate.toDateString(), 
        inOmerPeriod 
      });
      setIsOmerPeriod(inOmerPeriod);
      
      if (inOmerPeriod) {
        // Calculate weeks since Pesach
        const weeksSincePesach = Math.floor((today.getTime() - pesachDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
        console.log('Weeks since Pesach:', weeksSincePesach);
        
        // There are 6 chapters in Pirkei Avot
        // We start with chapter 1 on the first week after Pesach
        // The first week after Pesach is week 0, so we don't add 1
        const chapter = weeksSincePesach;
        console.log('Calculated chapter:', chapter);
        
        // Ensure chapter is between 1 and 6
        const boundedChapter = Math.min(Math.max(chapter, 1), 6);
        console.log('Bounded chapter:', boundedChapter);
        
        // Convert to Hebrew numerals (א=1, ב=2, ג=3, ד=4, ה=5, ו=6)
        const hebrewNumerals = ['א', 'ב', 'ג', 'ד', 'ה', 'ו'];
        const hebrewChapter = hebrewNumerals[boundedChapter - 1];
        console.log('Hebrew chapter:', hebrewChapter);
        
        setPirkeiAvotChapter(`פרק ${hebrewChapter}`);
      } else {
        console.log('Outside Omer period - clearing Pirkei Avot chapter');
        setPirkeiAvotChapter("");
      }
    };
    
    calculatePirkeiAvotChapter();
    
    // Calculate time until next Saturday midnight
    const getNextSaturdayMidnight = () => {
      const now = new Date();
      const daysUntilSaturday = (6 - now.getDay() + 7) % 7; // Days until next Saturday
      const nextSaturday = new Date(now);
      nextSaturday.setDate(now.getDate() + daysUntilSaturday);
      nextSaturday.setHours(0, 0, 0, 0);
      return nextSaturday;
    };
    
    const nextSaturday = getNextSaturdayMidnight();
    const timeUntilNextSaturday = nextSaturday.getTime() - new Date().getTime();
    
    const timeoutId = setTimeout(() => {
      calculatePirkeiAvotChapter();
      // Set up weekly updates at Saturday midnight
      setInterval(calculatePirkeiAvotChapter, 7 * 24 * 60 * 60 * 1000);
    }, timeUntilNextSaturday);
    
    return () => clearTimeout(timeoutId);
  }, []);
  
  console.log('MarqueeBar render state:', { isOmerPeriod, pirkeiAvotChapter });
  
  return (
    <div className="bg-white/70 backdrop-blur-sm py-2 sm:py-3 md:py-4 lg:py-5 relative overflow-hidden shadow-md">
      <div className="relative w-full overflow-hidden">
        <div className="flex items-center animate-marquee whitespace-nowrap">
          {/* Logo in marquee if enabled */}
          {titleConfig?.logo?.enabled && titleConfig?.logo?.url && (
            <div className="mx-8">
              <img 
                src={titleConfig.logo.url} 
                alt="Logo" 
                className="h-8 sm:h-10 md:h-12 lg:h-14 object-contain"
                style={{ 
                  maxWidth: titleConfig.logo.maxWidth ? `${titleConfig.logo.maxWidth}px` : '100px'
                }}
              />
            </div>
          )}
          
          {/* Only show Sefirat HaOmer during Omer period */}
          {isOmerPeriod && (
            <div className="flex items-center mx-8">
              <div className="font-bold text-black text-2xl sm:text-3xl md:text-5xl lg:text-6xl text-shadow-md">ספירת העומר:</div>
              <div className="simplified-omer whitespace-nowrap mr-8">
                <SefiratHaOmerMarquee />
              </div>
            </div>
          )}
          
          {/* Add divider only if Omer period is active */}
          {isOmerPeriod && (
            <div className="h-12 w-1 bg-black/20 mx-8 rounded-full"></div>
          )}
          
          <div className="flex items-center mx-8">
            <div className="font-bold text-black text-2xl sm:text-3xl md:text-5xl lg:text-6xl text-shadow-md">פרשת השבוע:</div>
            <div className="text-black text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-shadow-md mr-8">{parashaText || 'טוען...'}</div>
          </div>

          {/* Only show Pirkei Avot during Omer period */}
          {pirkeiAvotChapter && isOmerPeriod && (
            <>
              <div className="h-12 w-1 bg-black/20 mx-8 rounded-full"></div>
              <div className="flex items-center mx-8">
                <div className="font-bold text-black text-2xl sm:text-3xl md:text-5xl lg:text-6xl text-shadow-md">פרקי אבות:</div>
                <div className="text-black text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-shadow-md mr-8">
                  {pirkeiAvotChapter}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Simplified version of Sefirat HaOmer for the marquee
const SefiratHaOmerMarquee: React.FC = () => {
  const [hebrewText, setHebrewText] = useState<string>("");
  const [nextTzeitTime, setNextTzeitTime] = useState<Date | null>(null);

  // Hebrew translations for numbers
  const hebrewNumbers = ["אחד", "שניים", "שלושה", "ארבעה", "חמישה", "שישה", "שבעה", "שמונה", "תשעה", "עשרה"];
  const hebrewTeens = ["אחד עשר", "שניים עשר", "שלושה עשר", "ארבעה עשר", "חמישה עשר", "שישה עשר", "שבעה עשר", "שמונה עשר", "תשעה עשר"];
  const hebrewTens = ["עשרים", "שלושים", "ארבעים"];

  // Convert number to Hebrew text
  const numberToHebrewText = (num: number): string => {
    if (num <= 0) return "";
    if (num <= 10) return hebrewNumbers[num - 1];
    if (num <= 19) return hebrewTeens[num - 11];
    
    const tens = Math.floor(num / 10);
    const ones = num % 10;
    
    if (ones === 0) {
      return hebrewTens[tens - 2];
    } else {
      return `${hebrewTens[tens - 2]} ו${hebrewNumbers[ones - 1]}`;
    }
  };

  // Fetch Tzeit Hakochavim (nightfall) time for today
  const fetchTzeitTime = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      try {
        const { data, error } = await supabase.functions.invoke('zmanim', {
          body: { 
            date: today, 
            lat: HADERA_COORDINATES.lat, 
            lng: HADERA_COORDINATES.lng,
            tzid: HADERA_COORDINATES.tzid || "Asia/Jerusalem"
          }
        });
        
        if (error) {
          console.error("Error fetching zmanim:", error);
          return getFallbackTzeitTime(today);
        }
        
        if (data && data.times && data.times.tzeit_hakochavim) {
          // Parse the time (assuming format is HH:MM)
          const [hours, minutes] = data.times.tzeit_hakochavim.split(':').map(Number);
          
          // Create date for today at Tzeit time
          const tzeitDate = new Date();
          tzeitDate.setHours(hours, minutes, 0, 0);
          
          // If Tzeit is in the past for today, get tomorrow's Tzeit
          if (tzeitDate < new Date()) {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            const tomorrowStr = tomorrow.toISOString().split('T')[0];
            
            try {
              const tomorrowResp = await supabase.functions.invoke('zmanim', {
                body: { 
                  date: tomorrowStr, 
                  lat: HADERA_COORDINATES.lat, 
                  lng: HADERA_COORDINATES.lng,
                  tzid: HADERA_COORDINATES.tzid || "Asia/Jerusalem"
                }
              });
              
              if (!tomorrowResp.error && tomorrowResp.data?.times?.tzeit_hakochavim) {
                const [tHours, tMinutes] = tomorrowResp.data.times.tzeit_hakochavim.split(':').map(Number);
                const nextTzeit = new Date(tomorrow);
                nextTzeit.setHours(tHours, tMinutes, 0, 0);
                return nextTzeit;
              } else {
                // If tomorrow's API call fails, use fallback
                return getFallbackTzeitTime(tomorrowStr);
              }
            } catch (tomorrowError) {
              console.error("Error fetching tomorrow's zmanim:", tomorrowError);
              return getFallbackTzeitTime(tomorrowStr);
            }
          }
          
          return tzeitDate;
        }
        
        return getFallbackTzeitTime(today);
      } catch (apiError) {
        console.error("Error fetching zmanim from API:", apiError);
        return getFallbackTzeitTime(today);
      }
    } catch (error) {
      console.error("Error in fetchTzeitTime:", error);
      return getFallbackTzeitTime();
    }
  };
  
  // Fallback function to calculate Tzeit based on current date and average sunset time
  const getFallbackTzeitTime = (dateStr?: string): Date => {
    console.log("Using fallback Tzeit calculation");
    
    // Create a date object from the string or use today
    const date = dateStr ? new Date(dateStr) : new Date();
    
    // Determine season-based approximate sunset times for Israel (summer/winter)
    // These times are approximations for Hadera, Israel
    const month = date.getMonth(); // 0-indexed: 0 = January, 11 = December
    
    let sunsetHour: number;
    let sunsetMinute: number;
    
    // Summer months (April to September)
    if (month >= 3 && month <= 8) {
      // Later sunset in summer
      sunsetHour = 19; // 7:00 PM
      sunsetMinute = 30;
    } else {
      // Earlier sunset in winter
      sunsetHour = 17; // 5:00 PM
      sunsetMinute = 0;
    }
    
    // Tzeit is approximately 25 minutes after sunset
    const tzeitDate = new Date(date);
    tzeitDate.setHours(sunsetHour, sunsetMinute + 25, 0, 0);
    
    // If the fallback time is already in the past for today, use tomorrow
    if (tzeitDate < new Date() && dateStr === new Date().toISOString().split('T')[0]) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return getFallbackTzeitTime(tomorrow.toISOString().split('T')[0]);
    }
    
    return tzeitDate;
  };

  const calculateOmer = () => {
    try {
      // Get today's Hebrew date
      const today = new Date();
      const hdate = new HDate(today);
      
      // Calculate Omer day - starting from 16 Nissan (second day of Pesach)
      const secondDayPesach = new HDate(16, 1, hdate.getFullYear()); // 16 Nissan
      let omerDay = hdate.abs() - secondDayPesach.abs() + 2; // Add 2 to get the correct day
      
      // Check if we're after Tzeit Hakochavim (nightfall) - if not, subtract 1 from count
      const now = new Date();
      const tzeitHakochavim = nextTzeitTime;
      // If we have Tzeit time and current time is before Tzeit, we're still on previous day's count
      if (tzeitHakochavim && now < tzeitHakochavim) {
        omerDay--;
      }
      
      // If we're not in the Omer period (between Pesach and Shavuot), return empty string
      if (omerDay < 1 || omerDay > 49) {
        console.log('SefiratHaOmerMarquee: Outside Omer period, hiding component');
        setHebrewText("");
        return;
      }
      
      // Create display text in Hebrew using words instead of digits
      const dayText = numberToHebrewText(omerDay);
      setHebrewText(`היום ${dayText} יום לעומר`);
    } catch (error) {
      console.error('Error calculating Omer count:', error);
      setHebrewText("");
    }
  };

  // Initialize Omer counter and set up automatic refresh at Tzeit
  useEffect(() => {
    // Initial calculation
    calculateOmer();
    
    // Function to set up the next update timer
    const setupNextUpdate = async () => {
      try {
        // Get next Tzeit Hakochavim time
        const tzeitTime = await fetchTzeitTime();
        setNextTzeitTime(tzeitTime);
        
        if (tzeitTime) {
          const now = new Date();
          const timeUntilTzeit = tzeitTime.getTime() - now.getTime();
          
          // If somehow we get a negative time (tzeitTime is in the past), use fallback logic
          if (timeUntilTzeit <= 0) {
            console.warn("Calculated Tzeit time is in the past, using hourly check instead");
            return setInterval(calculateOmer, 60 * 60 * 1000);
          }
          
          console.log(`MarqueeBar Omer will update at Tzeit Hakochavim: ${tzeitTime.toLocaleTimeString()} (${timeUntilTzeit / 60000} minutes from now)`);
          
          // Set timeout to update at Tzeit
          const updateTimeout = setTimeout(() => {
            console.log('Updating MarqueeBar Omer count at Tzeit Hakochavim');
            calculateOmer();
            // Set up next day's update
            setupNextUpdate();
          }, timeUntilTzeit);
          
          return updateTimeout;
        } else {
          // Fallback to checking every hour if we can't get Tzeit time
          console.warn("Could not determine Tzeit time, using hourly check instead");
          const hourlyInterval = setInterval(calculateOmer, 60 * 60 * 1000);
          return hourlyInterval;
        }
      } catch (error) {
        console.error("Error in setupNextUpdate:", error);
        // Fallback to hourly checks in case of any error
        return setInterval(calculateOmer, 60 * 60 * 1000);
      }
    };
    
    // Start the update cycle
    let timeoutId: ReturnType<typeof setTimeout>;
    setupNextUpdate().then(id => {
      timeoutId = id as unknown as ReturnType<typeof setTimeout>;
    });
    
    // Also update every hour just as a backup
    const backupInterval = setInterval(calculateOmer, 60 * 60 * 1000);
    
    return () => {
      clearTimeout(timeoutId);
      clearInterval(backupInterval);
    };
  }, []);

  if (!hebrewText) return null;

  return (
    <div className="text-black text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-shadow-md">
      {hebrewText}
    </div>
  );
};

export default MarqueeBar;
